import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/utils-app";
import { articleRegion } from "../lib/article-utils";
import Seo, { ld } from "../components/Seo";
import { Building2, MapPin, Gauge, Clock, ExternalLink } from "lucide-react";
import AdBlock from "../components/AdBlock";

export default function Institution() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/institutions/${slug}`).then((r) => {
      setData(r.data);
    }).catch(() => setData({ institution: null, articles: [] }));
  }, [slug]);

  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-[var(--io-muted)]">Loading institution…</div>;
  if (!data.institution) return <div className="max-w-3xl mx-auto px-6 py-20 text-[var(--io-text)]">Institution not found.</div>;

  const ins = data.institution;

  return (
    <div data-testid="institution-page">
      <Seo
        title={ins.name}
        description={`${ins.name} — ${ins.region} ${ins.source_type}. Live arbitration updates and institutional background tracked by IndiaODR. Reliability ${ins.reliability}/10.`}
        path={`/institution/${slug}`}
        jsonLd={ld.breadcrumb(typeof window !== "undefined" ? window.location.origin : "", [
          { name: "Home", path: "/" },
          { name: "Institutions", path: "/institutions" },
          { name: ins.name },
        ])}
      />
      <section className="intel-grid border-b border-[var(--io-border)]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--io-muted)] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[var(--io-gold)]">Home</Link>
            <span>/</span>
            <Link to="/institutions" className="hover:text-[var(--io-gold)]">Institutions</Link>
            <span>/</span>
            <span className="text-[var(--io-text-2)]">{ins.name}</span>
          </nav>

          <div className="flex items-center gap-3 mb-4">
            <span className="w-12 h-12 rounded-md border border-[var(--io-gold)]/40 bg-gradient-to-br from-[var(--io-gold)]/15 to-transparent flex items-center justify-center">
              <Building2 size={22} className="text-[var(--io-gold)]" />
            </span>
            <div>
              <div className="eyebrow">{ins.source_type} · {ins.region}</div>
              <h1 className="font-serif text-[38px] md:text-[52px] leading-[1.05] text-[var(--io-text)] mt-1">{ins.name}</h1>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="io-badge"><MapPin size={11}/> {ins.region}</span>
            <span className="io-badge io-badge-gold"><Gauge size={11}/> Reliability {ins.reliability}/10</span>
            <span className="io-badge"><Clock size={11}/> Fetches every {ins.fetch_frequency_hours}h</span>
            {ins.topics?.map((t) => <span key={t} className="io-badge">{t}</span>)}
          </div>

          {ins.url && (
            <a href={ins.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[12px] text-[var(--io-gold)] hover:underline" data-testid="institution-external-url">
              Official source <ExternalLink size={12}/>
            </a>
          )}

          {ins.copyright_notes && (
            <p className="mt-4 text-[12px] text-[var(--io-muted)] italic max-w-2xl">Note: {ins.copyright_notes}</p>
          )}
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-10 md:py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <h2 className="font-serif text-[28px] md:text-[36px] text-[var(--io-text)] mb-6">Recent coverage</h2>
          <div className="divide-y divide-[var(--io-border)]">
            {data.articles.map((a) => (
              <Link key={a.id} to={`/article/${a.slug}`} className="block py-5 group" data-testid={`ins-article-${a.slug}`}>
                <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-muted)]">
                  <span className="text-[var(--io-gold)]">{a.category}</span>
                  <span>·</span><span>{articleRegion(a)}</span>
                  <span>·</span><span>{fmtDate(a.published_at)}</span>
                  <span>·</span><span>{a.read_minutes || 4} min</span>
                </div>
                <h3 className="font-serif text-[20px] md:text-[22px] mt-1.5 text-[var(--io-text)] group-hover:text-[var(--io-gold)]">{a.headline}</h3>
                <p className="mt-1.5 text-[14px] text-[var(--io-text-2)] line-clamp-2 max-w-[72ch]">{a.summary}</p>
              </Link>
            ))}
            {data.articles.length === 0 && <div className="py-10 text-[var(--io-muted)]">No articles yet for this institution.</div>}
          </div>
        </div>
        <aside className="lg:col-span-4 space-y-5">
          <AdBlock variant="sidebar" />
          <div className="io-card-el p-5">
            <div className="eyebrow mb-2">Institution profile</div>
            <p className="text-[13px] text-[var(--io-text-2)] leading-relaxed">
              IndiaODR monitors {ins.name}'s published updates. Editorial coverage is source-backed and human-reviewed.
              Reach out via <Link to="/contact" className="text-[var(--io-gold)] hover:underline">Contact</Link> to submit tips or corrections.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
